/**
 * Parses the reason that a user types before a data download.
 *
 * A download of candidate or employee data must record why the user downloaded it. The download
 * endpoint gives the reason to the privacy audit log action. This file makes that reason a typed
 * value:
 *
 * 1. `parseDownloadReason` trims the raw text from the reason input.
 * 2. It rejects empty text and text longer than `MAX_DOWNLOAD_REASON_LENGTH`.
 * 3. It returns a branded `DownloadReason`, so a download call cannot accept unchecked text.
 *
 * `DownloadReasonDialog.tsx` is the form boundary that calls this parser.
 */

import { err, ok, type Result } from 'neverthrow';

/** Largest reason length. It keeps one audit log entry short enough to read in a table cell. */
export const MAX_DOWNLOAD_REASON_LENGTH = 500;

/**
 * Keeps `DownloadReason` apart from a plain `string` in the type system.
 *
 * The symbol has no value at run time. Only `parseDownloadReason` can make a `DownloadReason`.
 */
declare const downloadReasonBrand: unique symbol;

/** Trimmed, non-empty text of at most `MAX_DOWNLOAD_REASON_LENGTH` characters. */
export type DownloadReason = string & { readonly [downloadReasonBrand]: true };

/** Says why raw text is not a download reason. The dialog keeps its download button disabled. */
export type InvalidDownloadReason = { type: 'DOWNLOAD_REASON_EMPTY' } | { type: 'DOWNLOAD_REASON_TOO_LONG' };

/** Parses raw input text into a `DownloadReason`. It keeps the reason invariant in one place. */
export const parseDownloadReason = (rawReason: string): Result<DownloadReason, InvalidDownloadReason> => {
  const trimmedReason = rawReason.trim();
  if (trimmedReason.length === 0) {
    return err({ type: 'DOWNLOAD_REASON_EMPTY' });
  }
  if (trimmedReason.length > MAX_DOWNLOAD_REASON_LENGTH) {
    return err({ type: 'DOWNLOAD_REASON_TOO_LONG' });
  }
  // SAFETY: trimmedReason is non-empty and at most MAX_DOWNLOAD_REASON_LENGTH characters.
  return ok(trimmedReason as DownloadReason);
};
